import { c as _c } from "react/compiler-runtime";
import { useToast } from "@flat-ui";
import { useAppDispatch } from "@store/store-helper";
import { resetSplashScreenStatus } from "@store/user/user-slice";
import { setFueFlags } from "@store/user/user-slice-thunk";
import { tryToClearCookies } from "@utils/cookie-utils";
import { ButtonEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback } from "react";
import { TEST_ERROR_MESSAGE } from "@components/dev-features-dialog/features";
import { sentryCaptureError } from "@utils/sentry-utils";
import { copyToClipboard } from "@utils/ui-utils";
import { generateGUID } from "@faro-lotv/foundation";
import { isLocalhost } from "@faro-lotv/foundation";
import { setIsTranslationOverrideAllowed, setIsSentryEnabled, setIsProductFeedbackEnabled } from "@store/dev-features/dev-features-slice";
import { useFeatureToggle } from "@components/dev-features-dialog/use-feature-toggle";
import { useAppParams } from "@router/router-helper";
import { runtimeConfig } from "@src/runtime-config";
import { getBooleanQueryParam } from "@utils/url-utils";
import { QueryParams } from "@router/route-params";

/** Type definition for the return value of the useDevTools hook */

/**
 * Custom hook that provides development tool actions.
 */
export function useDevTools() {
  const $ = _c(34);
  const {
    trackEvent
  } = useTrackEvent();
  const dispatch = useAppDispatch();
  const {
    openToast
  } = useToast();
  const {
    updateSearchParams
  } = useFeatureToggle();
  const {
    companyId
  } = useAppParams();
  let t0;
  if ($[0] !== companyId) {
    t0 = () => {
      const adminPanelUrl = runtimeConfig.urls.adminPanelUrl;
      const url = companyId ? `${adminPanelUrl}/company/${companyId}` : adminPanelUrl;
      window.open(url, "AdminPanel");
    };
    $[0] = companyId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const openAdminPanel = t0;
  let t1;
  if ($[2] !== trackEvent) {
    t1 = () => {
      const oldSize = document.cookie.length;
      tryToClearCookies();
      const newSize = document.cookie.length;
      window.alert(`Browser-accessible cookies cleared.\n\nOld size: ${oldSize} bytes\nNew size: ${newSize} bytes`);
      trackEvent({
        name: ButtonEvents.clearCookies,
        props: {
          oldSize,
          newSize,
          source: "FeaturesTable"
        }
      });
    };
    $[2] = trackEvent;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const clearCookies = t1;
  let t2;
  if ($[4] !== dispatch || $[5] !== openToast || $[6] !== trackEvent) {
    t2 = () => {
      trackEvent({
        name: ButtonEvents.resetFueFlags,
        props: {
          action: "resetFueFlags",
          source: "FeaturesTable"
        }
      });
      ;
      try {
        dispatch(resetSplashScreenStatus());
        dispatch(setFueFlags());
        openToast({
          title: "FUE flags reset successfully."
        });
      } catch (t3) {
        const error = t3;
        throw new Error(`Error resetting FUE flags: ${error}`, {
          cause: error
        });
      }
    };
    $[4] = dispatch;
    $[5] = openToast;
    $[6] = trackEvent;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const resetFueFlags = t2;
  let t3;
  if ($[8] !== openToast) {
    t3 = () => {
      ;
      try {
        const uuid = generateGUID();
        const errorMessage = `${TEST_ERROR_MESSAGE}: ${uuid}`;
        sentryCaptureError({
          error: new Error(errorMessage),
          title: TEST_ERROR_MESSAGE,
          data: {
            testErrorId: uuid
          }
        });
        openToast({
          title: TEST_ERROR_MESSAGE,
          message: `Test error sent to Sentry with the following UUID: ${uuid}`,
          persist: true,
          action: {
            label: "Copy",
            onClicked: () => {
              copyToClipboard(uuid);
              openToast({
                title: "UUID copied!",
                variant: "info"
              });
            }
          }
        });
      } catch (t4) {
        openToast({
          title: "Failed to send test error to Sentry",
          variant: "error"
        });
      }
    };
    $[8] = openToast;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const sendSentryTestErrorWithToast = t3;
  let t4;
  if ($[10] !== openToast || $[11] !== sendSentryTestErrorWithToast || $[12] !== trackEvent) {
    t4 = () => {
      trackEvent({
        name: ButtonEvents.sendTestSentryError,
        props: {
          action: "sendTestSentryError",
          source: "FeaturesTable"
        }
      });
      const isSentryEnabled = getBooleanQueryParam(globalThis.location.search, QueryParams.enableSentry);
      if (isLocalhost() && !isSentryEnabled) {
        openToast({
          title: "You can\u2019t send errors to Sentry while running on localhost.",
          variant: "warning"
        });
        return;
      }
      sendSentryTestErrorWithToast();
    };
    $[10] = openToast;
    $[11] = sendSentryTestErrorWithToast;
    $[12] = trackEvent;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const sendTestSentryError = t4;
  let t5;
  if ($[14] !== dispatch || $[15] !== trackEvent || $[16] !== updateSearchParams) {
    t5 = isEnabled => {
      trackEvent({
        name: ButtonEvents.changeLocalizeOverride,
        props: {
          isEnabled,
          source: "FeaturesTable"
        }
      });
      dispatch(setIsTranslationOverrideAllowed(isEnabled));
      updateSearchParams("allowTranslationOverride", isEnabled);
      window.location.reload();
    };
    $[14] = dispatch;
    $[15] = trackEvent;
    $[16] = updateSearchParams;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const allowLocalizeTranslations = t5;
  let t6;
  if ($[18] !== dispatch || $[19] !== trackEvent || $[20] !== updateSearchParams) {
    t6 = isEnabled_0 => {
      trackEvent({
        name: ButtonEvents.enableSentry,
        props: {
          isEnabled: isEnabled_0,
          source: "FeaturesTable"
        }
      });
      dispatch(setIsSentryEnabled(isEnabled_0));
      updateSearchParams("enableSentry", isEnabled_0);
      window.location.reload();
    };
    $[18] = dispatch;
    $[19] = trackEvent;
    $[20] = updateSearchParams;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const enableSentry = t6;
  let t7;
  if ($[22] !== dispatch || $[23] !== trackEvent || $[24] !== updateSearchParams) {
    t7 = isEnabled_1 => {
      trackEvent({
        name: ButtonEvents.enableProductFeedback,
        props: {
          isEnabled: isEnabled_1,
          source: "FeaturesTable"
        }
      });
      dispatch(setIsProductFeedbackEnabled(isEnabled_1));
      updateSearchParams(QueryParams.enableProductFeedback, isEnabled_1);
    };
    $[22] = dispatch;
    $[23] = trackEvent;
    $[24] = updateSearchParams;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const enableProductFeedback = t7;
  const themeMode = _temp;
  let t8;
  if ($[26] !== allowLocalizeTranslations || $[27] !== clearCookies || $[28] !== enableProductFeedback || $[29] !== enableSentry || $[30] !== openAdminPanel || $[31] !== resetFueFlags || $[32] !== sendTestSentryError) {
    t8 = {
      openAdminPanel,
      clearCookies,
      resetFueFlags,
      sendTestSentryError,
      allowLocalizeTranslations,
      enableSentry,
      enableProductFeedback,
      themeMode
    };
    $[26] = allowLocalizeTranslations;
    $[27] = clearCookies;
    $[28] = enableProductFeedback;
    $[29] = enableSentry;
    $[30] = openAdminPanel;
    $[31] = resetFueFlags;
    $[32] = sendTestSentryError;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  return t8;
}
function _temp() {}